import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { brl } from "@/lib/format";
import { can } from "@/lib/permissions";
import { Plus, Pencil, Trash2, Clock, Route } from "lucide-react";

const empty = { name: "", description: "", duration_minutes: 60, price: 0, commission_type: "percentage", commission_value: "", has_displacement: false, default_km: 0, price_per_km: 0, active: true };

export default function Services() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => setList((await api.get("/services")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = {
      ...form,
      duration_minutes: Number(form.duration_minutes),
      price: Number(form.price),
      commission_value: form.commission_value === "" ? null : Number(form.commission_value),
      default_km: Number(form.default_km || 0),
      price_per_km: Number(form.price_per_km || 0),
    };
    try {
      if (editing) await api.put(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Serviço salvo");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Falha ao salvar"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/services/${id}`); toast.success("Removido"); load(); }
    catch { toast.error("Falha ao remover"); }
  };

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">Serviços</h1>
          <p className="text-sm text-muted-foreground mt-1">Cadastre os serviços com preços, duração e comissões.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-service" onClick={openNew} className="h-11" disabled={!can("services.write")}><Plus className="h-4 w-4 mr-2" /> Novo serviço</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar serviço" : "Novo serviço"}</DialogTitle></DialogHeader>
            <div className="grid gap-4">
              <div className="space-y-2"><Label>Nome</Label><Input data-testid="input-service-name" value={form.name} onChange={(e) => set("name")(e.target.value)} /></div>
              <div className="space-y-2"><Label>Descrição</Label><Textarea data-testid="input-service-desc" value={form.description} onChange={(e) => set("description")(e.target.value)} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Duração (min)</Label><Input data-testid="input-service-duration" type="number" min="1" value={form.duration_minutes} onChange={(e) => set("duration_minutes")(e.target.value)} /></div>
                <div className="space-y-2"><Label>Preço (R$)</Label><Input data-testid="input-service-price" type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price")(e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de comissão</Label>
                  <Select value={form.commission_type} onValueChange={set("commission_type")}>
                    <SelectTrigger data-testid="select-commission-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentual (%)</SelectItem>
                      <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Valor da comissão <span className="text-muted-foreground text-xs">(opcional — usa o padrão do profissional se vazio)</span></Label><Input data-testid="input-commission-value" type="number" min="0" step="0.01" placeholder="Deixe vazio p/ usar padrão" value={form.commission_value} onChange={(e) => set("commission_value")(e.target.value)} /></div>
              </div>

              <div className="surface-muted p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2"><Route className="h-4 w-4" /> Deslocamento</div>
                    <p className="text-xs text-muted-foreground">Cobra por quilômetro rodado</p>
                  </div>
                  <Switch data-testid="switch-has-displacement" checked={form.has_displacement} onCheckedChange={set("has_displacement")} />
                </div>
                {form.has_displacement && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label>Valor por km (R$)</Label><Input data-testid="input-price-per-km" type="number" min="0" step="0.01" value={form.price_per_km} onChange={(e) => set("price_per_km")(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Km padrão</Label><Input data-testid="input-default-km" type="number" min="0" step="0.1" value={form.default_km} onChange={(e) => set("default_km")(e.target.value)} /></div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between">
                <Label>Ativo</Label>
                <Switch data-testid="switch-service-active" checked={form.active} onCheckedChange={set("active")} />
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="btn-save-service" onClick={save}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((s) => (
          <div key={s.id} data-testid={`service-card-${s.id}`} className="surface p-6 flex flex-col gap-3 transition-transform duration-300 hover:-translate-y-0.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-display font-semibold text-lg truncate">{s.name}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{s.description}</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded border ${s.active ? "border-primary/30 text-primary bg-primary/5" : "border-border text-muted-foreground"}`}>
                {s.active ? "Ativo" : "Inativo"}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="inline-flex items-center gap-1 text-muted-foreground"><Clock className="h-3.5 w-3.5" />{s.duration_minutes} min</span>
              <span className="font-semibold">{brl(s.price)}</span>
              <span className="text-muted-foreground">Com.: {s.commission_value == null ? "(padrão do profissional)" : (s.commission_type === "percentage" ? `${s.commission_value}%` : brl(s.commission_value))}</span>
            </div>
            {s.has_displacement && (
              <div className="text-xs text-accent-foreground/80 bg-accent/15 border border-accent/30 rounded-md px-2 py-1.5 inline-flex items-center gap-1 self-start">
                <Route className="h-3 w-3" /> {brl(s.price_per_km)}/km · padrão {s.default_km} km
              </div>
            )}
            <div className="flex gap-2 pt-2 border-t border-border">
              <Button data-testid={`btn-edit-service-${s.id}`} variant="ghost" size="sm" onClick={() => openEdit(s)} disabled={!can("services.write")}><Pencil className="h-4 w-4 mr-1" /> Editar</Button>
              {can("services.delete") && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button data-testid={`btn-delete-service-${s.id}`} variant="ghost" size="sm" className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4 mr-1" /> Excluir</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Excluir "{s.name}"?</AlertDialogTitle><AlertDialogDescription>Essa ação não pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => remove(s.id)}>Excluir</AlertDialogAction></AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="col-span-full surface p-12 text-center text-muted-foreground">Nenhum serviço cadastrado. Crie o primeiro!</div>
        )}
      </div>
    </div>
  );
}
